import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EditReport = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { getAuthHeader } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Settings from database
  const [settings, setSettings] = useState({
    veicoli: [],
    personale: [],
    turni_options: [],
    extratime_options: []
  });

  const [formData, setFormData] = useState({
    titolo_evento: '',
    data_evento: '',
    veicoli_usati: [],
    veicolo_altro: '',
    rimborsi_veicoli: '',
    danni_veicoli: '',
    turni_personale: [],
    altri_personale: '',
    extratime_ore: 0,
    materiale_extra: '',
    orario_partenza: '',
    orario_arrivo: '',
    orario_inizio_evento: '',
    orario_fine_evento: '',
    orario_fine_smontaggio: '',
    orario_rientro: '',
    riepilogo_spese: '',
    spese_altro: '',
    materiale_guasto: '',
    problemi_cliente: '',
    problemi_personale: ''
  });

  const [turniMap, setTurniMap] = useState({});

  useEffect(() => {
    const loadData = async () => {
      await fetchSettings();
      await fetchReport();
    };
    loadData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings`, {
        headers: getAuthHeader()
      });
      setSettings(response.data);
    } catch (error) {
      console.error('Errore caricamento impostazioni:', error);
    }
  };

  const fetchReport = async () => {
    try {
      const response = await axios.get(`${API}/reports/${id}`, {
        headers: getAuthHeader()
      });
      const report = response.data;
      
      setFormData({
        titolo_evento: report.titolo_evento || '',
        data_evento: report.data_evento || '',
        veicoli_usati: report.veicoli_usati || [],
        veicolo_altro: report.veicolo_altro || '',
        rimborsi_veicoli: report.rimborsi_veicoli || '',
        danni_veicoli: report.danni_veicoli || '',
        turni_personale: report.turni_personale || [],
        altri_personale: report.altri_personale || '',
        extratime_ore: report.extratime_ore || 0,
        materiale_extra: report.materiale_extra || '',
        orario_partenza: report.orario_partenza || '',
        orario_arrivo: report.orario_arrivo || '',
        orario_inizio_evento: report.orario_inizio_evento || '',
        orario_fine_evento: report.orario_fine_evento || '',
        orario_fine_smontaggio: report.orario_fine_smontaggio || '',
        orario_rientro: report.orario_rientro || '',
        riepilogo_spese: report.riepilogo_spese || '',
        spese_altro: report.spese_altro || '',
        materiale_guasto: report.materiale_guasto || '',
        problemi_cliente: report.problemi_cliente || '',
        problemi_personale: report.problemi_personale || ''
      });

      // Create turni map
      const map = {};
      if (report.turni_personale) {
        report.turni_personale.forEach(turno => {
          map[turno.nome] = turno.ore;
        });
      }
      setTurniMap(map);
    } catch (error) {
      console.error('Errore caricamento report:', error);
      alert('Errore durante il caricamento del report');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleVeicoliChange = (veicolo) => {
    setFormData(prev => {
      const veicoli = prev.veicoli_usati.includes(veicolo)
        ? prev.veicoli_usati.filter(v => v !== veicolo)
        : [...prev.veicoli_usati, veicolo];
      return { ...prev, veicoli_usati: veicoli };
    });
  };

  const handleTurnoChange = (persona, ore) => {
    setTurniMap(prev => {
      const newMap = { ...prev };
      if (ore === 0) {
        delete newMap[persona];
      } else {
        newMap[persona] = ore;
      }
      return newMap;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const turni_personale = Object.entries(turniMap).map(([nome, ore]) => ({
        nome,
        ore
      }));

      const reportData = {
        ...formData,
        turni_personale
      };

      await axios.put(`${API}/reports/${id}`, reportData, {
        headers: getAuthHeader()
      });

      alert('Report aggiornato con successo!');
      // Force page reload to show updated data
      window.location.href = `/report/${id}`;
    } catch (error) {
      console.error('Errore aggiornamento report:', error);
      alert('Errore durante l\'aggiornamento del report');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="edit-report-page">
      {/* Navbar */}
      <nav className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-800">BETTI SRL</h1>
                <p className="text-xs text-gray-500">Modifica Report Evento</p>
              </div>
            </div>
            <button
              onClick={() => navigate(`/report/${id}`)}
              className="px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-lg transition"
            >
              Annulla
            </button>
          </div>
        </div>
      </nav>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Informazioni Base */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">Informazioni Evento</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Titolo Evento <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.titolo_evento}
                  onChange={(e) => handleInputChange('titolo_evento', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                  data-testid="titolo-evento-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Data Evento <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  value={formData.data_evento}
                  onChange={(e) => handleInputChange('data_evento', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                  data-testid="data-evento-input"
                />
              </div>
            </div>
          </div>

          {/* Veicoli */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">Veicoli Usati</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Veicoli <span className="text-red-500">*</span>
                </label>
                <div className="space-y-2">
                  {(settings.veicoli || []).map((veicolo) => (
                    <label key={veicolo} className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer transition">
                      <input
                        type="checkbox"
                        checked={formData.veicoli_usati.includes(veicolo)}
                        onChange={() => handleVeicoliChange(veicolo)}
                        className="w-5 h-5 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                      />
                      <span className="text-gray-700">{veicolo}</span>
                    </label>
                  ))}
                  <div>
                    <input
                      type="text"
                      value={formData.veicolo_altro}
                      onChange={(e) => handleInputChange('veicolo_altro', e.target.value)}
                      placeholder="Altro veicolo (opzionale)"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Rimborsi per Veicoli Personali <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={formData.rimborsi_veicoli}
                  onChange={(e) => handleInputChange('rimborsi_veicoli', e.target.value)}
                  rows="3"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Danni/Problemi/Incidenti al Veicolo
                </label>
                <textarea
                  value={formData.danni_veicoli}
                  onChange={(e) => handleInputChange('danni_veicoli', e.target.value)}
                  rows="3"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
          </div>

          {/* Turni Personale */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">Personale su Evento</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Turni (seleziona turno per persona)
                </label>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nome</th>
                        {(settings.turni_options || []).map((turno) => (
                          <th key={turno} className="px-2 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">{turno}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {(settings.personale || []).map((persona) => (
                        <tr key={persona} className="hover:bg-gray-50">
                          <td className="px-4 py-3 text-sm font-medium text-gray-700">{persona}</td>
                          {(settings.turni_options || []).map((turno) => (
                            <td key={turno} className="px-2 py-3 text-center">
                              <input
                                type="radio"
                                name={`turno-${persona}`}
                                checked={turniMap[persona] === turno}
                                onChange={() => handleTurnoChange(persona, turno)}
                                className="w-4 h-4 text-blue-600 focus:ring-2 focus:ring-blue-500"
                              />
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nome e Cognome e Turno di Altri non in Lista
                </label>
                <textarea
                  value={formData.altri_personale}
                  onChange={(e) => handleInputChange('altri_personale', e.target.value)}
                  rows="3"
                  placeholder="Es: Mario Rossi - 4 turni"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Extratime (turni)
                </label>
                <div className="flex space-x-4">
                  {(settings.extratime_options || []).map((turno) => (
                    <label key={turno} className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="radio"
                        name="extratime"
                        value={turno}
                        checked={formData.extratime_ore === turno}
                        onChange={() => handleInputChange('extratime_ore', turno)}
                        className="w-4 h-4 text-blue-600 focus:ring-2 focus:ring-blue-500"
                      />
                      <span className="text-gray-700">{turno}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Materiale Extra Last Minute <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={formData.materiale_extra}
                  onChange={(e) => handleInputChange('materiale_extra', e.target.value)}
                  rows="3"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
              </div>
            </div>
          </div>

          {/* Orari */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">Orari</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Orario Partenza Magazzino <span className="text-red-500">*</span>
                </label>
                <input
                  type="time"
                  value={formData.orario_partenza}
                  onChange={(e) => handleInputChange('orario_partenza', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Orario Arrivo in Location
                </label>
                <input
                  type="time"
                  value={formData.orario_arrivo}
                  onChange={(e) => handleInputChange('orario_arrivo', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Orario Inizio Evento
                </label>
                <input
                  type="time"
                  value={formData.orario_inizio_evento}
                  onChange={(e) => handleInputChange('orario_inizio_evento', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Orario Fine Evento
                </label>
                <input
                  type="time"
                  value={formData.orario_fine_evento}
                  onChange={(e) => handleInputChange('orario_fine_evento', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Orario Fine Smontaggio
                </label>
                <input
                  type="time"
                  value={formData.orario_fine_smontaggio}
                  onChange={(e) => handleInputChange('orario_fine_smontaggio', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Orario Rientro <span className="text-red-500">*</span>
                </label>
                <input
                  type="time"
                  value={formData.orario_rientro}
                  onChange={(e) => handleInputChange('orario_rientro', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
              </div>
            </div>
          </div>

          {/* Spese e Problemi */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">Spese e Segnalazioni</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Riepilogo Spese <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={formData.riepilogo_spese}
                  onChange={(e) => handleInputChange('riepilogo_spese', e.target.value)}
                  rows="4"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Altro da Segnalare (Spese)
                </label>
                <textarea
                  value={formData.spese_altro}
                  onChange={(e) => handleInputChange('spese_altro', e.target.value)}
                  rows="3"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Materiale Guasto da Segnalare
                </label>
                <textarea
                  value={formData.materiale_guasto}
                  onChange={(e) => handleInputChange('materiale_guasto', e.target.value)}
                  rows="3"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Problemi con Cliente/Location/ecc
                </label>
                <textarea
                  value={formData.problemi_cliente}
                  onChange={(e) => handleInputChange('problemi_cliente', e.target.value)}
                  rows="3"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Problemi Comportamentali Personale
                </label>
                <textarea
                  value={formData.problemi_personale}
                  onChange={(e) => handleInputChange('problemi_personale', e.target.value)}
                  rows="3"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
          </div>

          {/* Submit */}
          <div className="flex justify-end space-x-4">
            <button
              type="button"
              onClick={() => navigate(`/report/${id}`)}
              className="px-6 py-3 bg-gray-100 text-gray-700 font-semibold rounded-lg hover:bg-gray-200 transition"
              disabled={saving}
            >
              Annulla
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-6 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="update-report-btn"
            >
              {saving ? 'Salvataggio in corso...' : 'Salva Modifiche'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditReport;
